import * as React from 'react';
import { Modal, ModalFooter, ModalTitle, ModalBody, ModalHeader } from '@common/components/modal';
import { I18n } from 'react-redux-i18n';

import { Flex } from '@common/components/Flex';
import { Button } from '@common/components/Button';
import { FormControl } from '@common/components/form-control';
import { Loading } from '@common/components/loading';
import { stringHasInteger } from '@common/utils/string';
import { LOOKUP_MODULE_NAME } from '../../LookupTable';

import './DataImport.styles.scss';

const DEFAULT_DELIMITER = '';

const IMPORT_TYPE = {
  tab: 'TXT_TAB',
  comma: 'TXT_COMMA',
  other: 'TXT_OTHER'
};

const IMPORT_MIME_TYPE = {
  [IMPORT_TYPE.tab]: '.txt',
  [IMPORT_TYPE.comma]: '.csv,.txt',
  [IMPORT_TYPE.other]: '.txt'
};

export const DataImport = (props) => {
  const { close, onImport } = props;
  const [importResponseMetadata, setImportResponseMetadata] = React.useState({  
    errors: [],
    warnings: [],
    total: null,
    processed: null,
  });
  const [selectedOptions, setSelectedOptions] = React.useState([]);
  const [delimiter, setDelimiter] = React.useState(DEFAULT_DELIMITER);
  const [importing, setImportingState] = React.useState(false);
  const [matchedColumnsAmount, setMatchedColumnsAmount] = React.useState('');
  const uploadInputRef = React.useRef();

  const settings = React.useMemo(() => {
    return [
      { label: 'Tab Delimited', id: IMPORT_TYPE.tab },
      { label: 'Comma Delimited', id: IMPORT_TYPE.comma },
      { label: 'Other (specify delimiter)', id: IMPORT_TYPE.other },
    ].map((x) => ({ ...x, label: I18n.t(x.label) }));
  }, []);

  const options = React.useMemo(() => {
    return [
      { label: 'Ignore column headings', metadata: { ignoreHeaders: true } },
    ].map((x) => ({ ...x, id: x.label, label: I18n.t(x.label) }));
  }, []);

  const [selectedSetting, setSelectedSetting] = React.useState(settings[0].id);

  const handleSettingsChange = React.useCallback((e) => {
    e.persist();

    const { value } = e.target;

    setSelectedSetting(value);

    setDelimiter(value === IMPORT_TYPE.other ? '' : DEFAULT_DELIMITER);
  }, [selectedSetting]);

  const handleOptionsChange = React.useCallback((e) => {
    e.persist();

    const { value } = e.target;

    const resultSettings = selectedOptions.includes(value)
      ? selectedOptions.filter((x) => x !== value)
      : [...selectedOptions, value];

    setSelectedOptions(resultSettings);
  }, [selectedOptions]);

  const handleDelimiterChange = React.useCallback((e) => {
    e.persist();

    const { value } = e.target;

    setDelimiter(value);
  }, []);

  const handleImport = React.useCallback(async (e) => {
    try {
      e.persist();

      const file = e.target.files[0];

      e.target.value = '';

      setImportingState(true);

      const optionsSubmitData = selectedOptions.reduce((acc, id) => {
        const { metadata } = options.find((x) => x.id === id);

        return {
          ...acc,
          ...metadata
        };
      }, {});

      const { entity: { payLoad } } = await onImport({
        fileData: file,
        delimiter: delimiter || undefined,
        importType: selectedSetting,
        nbMatchedColumns: Number(matchedColumnsAmount),
        ...optionsSubmitData,
      });

      setImportResponseMetadata(payLoad);
    } finally {
      setImportingState(false);
    }
  }, [onImport, options, selectedOptions, selectedSetting, delimiter, matchedColumnsAmount]);

  const handleMatchedColumnsAmountChange = React.useCallback((e) => {
    e.persist();

    const { value } = e.target;
    const isValid = stringHasInteger(value, { allowEmpty: true, allowNegative: false, allowZero: false });

    if (isValid) {
      setMatchedColumnsAmount(value);
    }
  }, []);

  const handleImportBtnClick = React.useCallback(() => {
    uploadInputRef.current.click();
  }, [uploadInputRef]);

  const exceptions = React.useMemo(() => {
    const value = [];

    if (importResponseMetadata.errors?.length) {
      value.push(...importResponseMetadata.errors);
    }

    if (importResponseMetadata.warnings?.length) {
      value.push(...importResponseMetadata.warnings);
    }

    return value;
  }, [importResponseMetadata]);

  const shouldShowWarning = React.useMemo(() => {
    const { total } = importResponseMetadata;

    return importing || Boolean(exceptions.length) || (total === 0 || total > 0);
  }, [importing, exceptions, importResponseMetadata]);

  const importBtnText = React.useMemo(() => {
    if (shouldShowWarning) {
      return 'Import more';
    }

    return 'Import';
  }, [shouldShowWarning]);

  const shouldShowCancelBtn = React.useMemo(() => {
    if (importResponseMetadata.processed > 0) {
      return false;
    }

    return true;
  }, [importResponseMetadata]);

  return (
    <Modal
      show
      backdrop="static"
      bsClass="bootstrap modal"
      className="DataImport"
      animation={false}
      onEscapeKeyDown={close}
      onHide={close}
    >
      <ModalHeader closeButton onHide={close} className="DataImport__header">
        <ModalTitle id={`${LOOKUP_MODULE_NAME}-import-data-header`}>
          {I18n.t('Import Lookup Data')}
        </ModalTitle>
      </ModalHeader>

      <ModalBody className="DataImport__content">
        <Flex alignItems="flex-start">
          <div className="DataImport__content__sections">

            {!shouldShowWarning && (
              <>
                <section className="DataImport__content__section">
                  <span className="DataImport__content__section__title">{I18n.t('Data Import Definition')}</span>

                  {settings.map((x) => (
                    <Flex key={x.id} alignItems="center" justify="flex-start">
                      <label className="DataImport__content__section__item">
                        <input 
                          id={`${LOOKUP_MODULE_NAME}-import-setting-${x.id}`} 
                          type="radio" 
                          value={x.id}
                          checked={selectedSetting === x.id}
                          name={`${LOOKUP_MODULE_NAME}-import-setting`} 
                          onChange={handleSettingsChange} 
                        />
                        {x.label}
                      </label>
                      {x.id === selectedSetting && x.id === IMPORT_TYPE.other && (
                        <FormControl
                          className="DataImport__content__section__delimiter-input"
                          value={delimiter}
                          onChange={handleDelimiterChange}
                        />
                      )}
                    </Flex>
                  ))}

                  <Flex className="DataImport__content__section__match-columns" alignItems="center" justify="flex-start">
                    <span>{I18n.t('Number of matched columns')}</span>
                    <FormControl
                      className="DataImport__content__section__matched-columns-input"
                      value={matchedColumnsAmount}
                      onChange={handleMatchedColumnsAmountChange}
                    />
                  </Flex>
                </section>

                <section>
                  {options.map((x) => (
                    <label key={x.id} className="DataImport__content__section__item">
                      <input 
                        id={`${LOOKUP_MODULE_NAME}-import-option-${x.id}`} 
                        type="checkbox" 
                        value={x.id}
                        checked={selectedOptions.includes(x.id)}
                        name={`${LOOKUP_MODULE_NAME}-import-option`} 
                        onChange={handleOptionsChange} 
                      />
                      {x.label}
                    </label>
                  ))}
                </section>
              </>
            )}

            {shouldShowWarning && (
              <section className="DataImport__content__section">
                <span className="DataImport__content__section__title">{I18n.t('Import Progress')}</span>

                {/* Progress Bar Section */}
                {(importing || (importResponseMetadata && importResponseMetadata.total > 0 && importResponseMetadata.processed !== importResponseMetadata.total)) && (
                  <div className="DataImport__progress">
                    <div className="DataImport__progress__bar">
                      <div 
                        className="DataImport__progress__bar__fill"
                        style={{ 
                          width: `${importResponseMetadata && importResponseMetadata.total > 0 
                            ? Math.max(5, (importResponseMetadata.processed / importResponseMetadata.total) * 100) 
                            : importing ? 10 : 0}%` 
                        }}
                      />
                    </div>
                    <div className="DataImport__progress__text">
                      {importing && (!importResponseMetadata || !importResponseMetadata.processed) && (
                        <span>{I18n.t('Starting import...')}</span>
                      )}
                      {importResponseMetadata && importResponseMetadata.processed > 0 && (
                        <span>
                          {I18n.t('Processing...')} {importResponseMetadata.processed} / {importResponseMetadata.total} 
                          ({Math.round((importResponseMetadata.processed / importResponseMetadata.total) * 100)}%)
                        </span>
                      )}
                      {importResponseMetadata && importResponseMetadata.total > 0 && importResponseMetadata.processed === importResponseMetadata.total && (
                        <span>{I18n.t('Import completed successfully!')}</span>
                      )}
                    </div>
                  </div>
                )}

                {/* Loading Spinner */}
                {importing && importResponseMetadata && importResponseMetadata.total > 0 && importResponseMetadata.processed !== importResponseMetadata.total && (
                  <Flex className="DataImport__loader" justify="center" alignItems="center">
                    <Loading size={30} />
                    <span className="DataImport__loader__text">{I18n.t('Importing data, please wait...')}</span>
                  </Flex>
                )}

                {exceptions.length > 0 && (
                  <div className="DataImport__errors">
                    <h5 className="DataImport__content__section__title DataImport__content__section__title--with-margin">{I18n.t('Import Errors and Warnings')}</h5>
                    {exceptions.map((error, index) => (
                      <div key={index} className="DataImport__error-item">
                        {error}
                      </div>
                    ))}
                  </div>
                )}
              </section>
            )}
          </div>
          <div className="DataImport__content__controls">
            {!importing && !importResponseMetadata.processed && (
              <Button
                className="DataImport__content__controls__btn"
                disabled={(IMPORT_TYPE.other === selectedSetting && !delimiter) || importing}
                id={`${LOOKUP_MODULE_NAME}-import-button`} 
                onClick={handleImportBtnClick}
              >
                {I18n.t(importBtnText)}
              </Button>
            )}
            <input 
              hidden
              multiple={false}
              accept={IMPORT_MIME_TYPE[selectedSetting]}
              ref={uploadInputRef}
              className="DataImport__content__controls__upload-input" 
              type="file" 
              onChange={handleImport} 
            />
            {shouldShowCancelBtn && (
              <Button 
                className="DataImport__content__controls__btn"
                disabled={importing}
                id={`${LOOKUP_MODULE_NAME}-import-button`} 
                onClick={close}
              >
                {I18n.t('Cancel')}
              </Button>
            )}
            {importResponseMetadata.processed > 0 && (
              <Button
                className="DataImport__content__controls__btn"
                disabled={(IMPORT_TYPE.other === selectedSetting && !delimiter)}
                id={`${LOOKUP_MODULE_NAME}-import-button`} 
                onClick={close}
              >
                {I18n.t('Ok')}
              </Button>
            )}
          </div>
        </Flex>
      </ModalBody>
    </Modal>
  );
};